import * as React from "react";
import Slider from "react-slick";
import { Project } from "../../core/api/models/project";
import { ViewTypes } from "./props";
import moment from "moment";
import { convertPascalCaseToWords } from "../../core";
import TenancyTimeline from "./TenancyTimeline";
import { useHistory } from "react-router-dom";
import { RouteKeys } from "../routes/route-keys";

const PropertyUnit: React.FunctionComponent<{
  project?: Project;
  availableUnit?: number;
  allUnit?: number;
  onView?: (id: string) => void;
}> = (props) => {
  const history = useHistory();
  const [viewType, setViewType] = React.useState<ViewTypes>(ViewTypes.List);
  const [expandedRows, setExpandedRows] = React.useState<{
    [key: number]: boolean;
  }>({});
  const settings = {
    className: "require-slider",
    dots: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
  };

  const floors = [...new Set(props.project?.unitDetails?.map((f) => f.floorName))].sort();

  const ordinalSuffixOf = (i: number) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
      return i + "st";
    }
    if (j === 2 && k !== 12) {
      return i + "nd";
    }
    if (j === 3 && k !== 13) {
      return i + "rd";
    }
    return i + "th";
  };

  const toggleRow = (rowId: number) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [rowId]: !prevExpandedRows[rowId],
    }));
  };

  const timeDiffInPercentage = (sTime: Date, eTime: Date) => {
    const totalDuration = moment(eTime).diff(moment(sTime)); // in milliseconds
    const elapsedDuration = moment().diff(moment(sTime)); // in milliseconds

    let percentage;

    if (elapsedDuration >= totalDuration) {
      percentage = 100;
    } else if (elapsedDuration <= 0) {
      percentage = 0;
    } else {
      percentage = (elapsedDuration / totalDuration) * 100;
    }
    return `${percentage.toFixed(2)}%`;
  };

  const findCompanyName = (uName: string, tenantDetails: any) => {
    if (tenantDetails?.length > 0 && uName) {
      const companyName =
        tenantDetails?.find((item: { unitName: string; tenantsName: any }) => {
          return item?.unitName === uName && (item?.tenantsName || "");
        })?.tenantsName || "";
      return companyName;
    }
  };
  const findCurrentStatus = (uName: string, tenantDetails: any) => {
    if (tenantDetails?.length > 0 && uName) {
      const currentStatus =
        tenantDetails?.find((item: { unitName: string; tenantsName: any }) => {
          return item?.unitName === uName
        })?.currentStatus || "";
      return currentStatus;
    }
  };
  const findLeasePeriod = (uName: string, tenantDetails: any) => {
    if (tenantDetails?.length > 0 && uName) {
      const leasePeriod = tenantDetails?.find((item: { unitName: string }) => {
        return item?.unitName === uName;
      });
      const startDate = leasePeriod?.tenantStartDate || "";
      const endDate = leasePeriod?.tenantEndDate || "";
      return startDate && endDate
        ? `${moment(startDate).format("DD-MM-YYYY")} - ${moment(endDate).format("DD-MM-YYYY")}`
        : "";
    }
  };

  return (
    <>
      <div className="pt-2">
        <div className="container-fluid">
          <div className="row mb-5">
            <div className="col-md-6 col-12 pl-0 pr-5 d-flex align-items-center mb-3">
              <span className="font-sm font-weight-medium">View by:</span>
              <div
                className="tabButton tabButton--variant d-flex w-100 text-center font-md font-weight-semibold cursor-pointer ml-2"
                style={{ maxWidth: "353px" }}
              >
                <div
                  className={`tabButton__item flex-fill ${
                    viewType === ViewTypes.List ? "tabButton__item--active" : ""
                  }`}
                  onClick={() => setViewType(ViewTypes.List)}
                >
                  List
                </div>
                {!!props?.project?.tenantDetails?.length && (
                  <div
                    className={`tabButton__item flex-fill ${
                      viewType === ViewTypes.Occupation ? "tabButton__item--active" : ""
                    }`}
                    onClick={() => setViewType(ViewTypes.Occupation)}
                  >
                    Occupation
                  </div>
                )}

                <div
                  className={`tabButton__item flex-fill ${
                    viewType === ViewTypes.Timeline ? "tabButton__item--active" : ""
                  }`}
                  onClick={() => setViewType(ViewTypes.Timeline)}
                >
                  Timeline
                </div>
              </div>
            </div>

            {ViewTypes.List === viewType && (
              <>
                <div className="col-12 mt-2">
                  <div className="py-4 px-2" style={{ maxWidth: "90vw" }}>
                    <Slider {...settings}>
                      <div className="border rounded p-2 d-inline-block mr-1 w-auto">
                        <div className="font-sm font-weight-semibold title">Matching Unit</div>
                        <div className="font-xs font-weight-medium text-gray-700">
                          {
                            props.project?.unitDetails?.filter((x) =>
                              props.project?.properties?.find(
                                (p) =>
                                  p.propertyID?.toString() === x.propertyID?.toString() &&
                                  p.propertyAvailableStatus === "available"
                              )
                            )?.length
                          }{" "}
                          Available, {props.project?.unitDetails?.length} Total
                        </div>
                      </div>
                      {floors?.map((fl) => {
                        const availableCount = props.project?.unitDetails?.filter(
                          (x) =>
                            props.project?.properties?.find(
                              (p) =>
                                p.propertyID?.toString() === x.propertyID?.toString() &&
                                p.propertyAvailableStatus === "available"
                            ) && x.floorName === fl
                        )?.length;
                        const totalCount = props.project?.unitDetails?.filter(
                          (x) => x.floorName === fl
                        )?.length;

                        return (
                          <>
                            <div className="border rounded p-2 d-inline-block mr-1 w-auto">
                              <div className="font-sm font-weight-semibold title">
                                {fl === 0 ? (
                                  <>Ground Floor</>
                                ) : (
                                  <>{ordinalSuffixOf(Number(fl))} Floor</>
                                )}
                              </div>
                              <div className="font-xs font-weight-medium text-gray-700">
                                {availableCount} Available, {totalCount} Total
                              </div>
                            </div>
                          </>
                        );
                      })}
                    </Slider>
                  </div>
                </div>
                <div className="col-12 px-0">
                  <div className="table-responsive">
                    <table className="table table-bordered w-100">
                      <tr>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Floor</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Unit Id</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1"> {props?.project?.projectTypeName === "Business Center & Co-working Space" ? "Number Of Seats" : "Area (in Sq.Ft)"}</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Interior Type</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Quoted Rent</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Current Status</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Company Name</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Lease Period</div>
                        </td>
                        <td className="py-2 border-bottom font-sm font-weight-semibold">
                          <div className="py-1">Available From </div>
                        </td>
                      </tr>
                      {props.project?.unitDetails?.map((unit) => {
                        const property = unit.propertyID
                          ? props.project?.properties?.find(
                              (p) => p.propertyID?.toString() === unit.propertyID?.toString()
                            )
                          : undefined;
                        const propertyDetails = unit.propertyID
                          ? props.project?.propertyDetails?.find(
                              (p) => p.propertyID?.toString() === unit.propertyID?.toString()
                            )
                          : undefined;

                        const tenantDetails =
                          props?.project?.tenantDetails && props.project.tenantDetails.length > 0
                            ? props.project.tenantDetails.map((item) => item?.unitDetails).flat()
                            : [];

                        return (
                          <>
                            <tr
                            className={property?.propertyAvailableStatus === "available"?'cursor-pointer':''}
                              onClick={() => {
                                if (property?.propertyAvailableStatus === "available") {
                                  props?.onView?.(unit?.propertyId!);
                                }
                              }}
                            >
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">{unit.floorName}</div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">{unit.unitName}</div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">{unit.unitArea}</div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">{property?.attribute?.interiorType}</div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">
                                  {property?.propertyAvailableStatus === 'available' ?
                                    property?.attribute?.quotedRentPerSqFtPerMonth :
                                    ''}{" "}
                                </div>
                              </td>
                              <td
                                className="py-3 border-bottom font-sm font-weight-semibold "
                              >
                                <div className="">
                                  { convertPascalCaseToWords( findCurrentStatus(unit.unitName, tenantDetails) ) }
                                </div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">
                                  {findCompanyName(unit.unitName, tenantDetails)}
                                </div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                <div className="">
                                  {findLeasePeriod(unit.unitName, tenantDetails)}
                                </div>
                              </td>
                              <td className="py-3 border-bottom font-sm font-weight-semibold">
                                {property?.availablefromdate &&
                                  property?.availablefromdate !== "1970-01-01T00:00:00.000Z" && (
                                    <div className="">
                                      {moment(property?.availablefromdate).format("DD-MM-YYYY")}
                                    </div>
                                  )}
                              </td>
                            </tr>
                          </>
                        );
                      })}
                    </table>
                  </div>
                </div>
              </>
            )}

            {ViewTypes.Occupation === viewType && (
              <>
                {props?.project?.tenantDetails?.length ? (
                  props?.project?.tenantDetails?.map((td: any) => (
                    <div className="col-12 px-0 d-flex">
                      <div className="font-xs pt-2 mt-1" style={{ width: "24px", minWidth: "24px" }}>
                        {td.floorNum === 0 ? "G" : td.floorNum}
                      </div>
                      {td?.unitDetails?.length ? (
                        <div className="d-flex flex-wrap flex-fill">
                          {td?.unitDetails?.map((ud: any) => (
                            <>
                              <div className="flex-fill py-1">
                                <div
                                  className={`${
                                    ud.currentStatus === "Available" ? "bg-t-grey" : "bg-primary"
                                  } text-white text-center font-sm font-weight-bold px-4 py-2 rounded-sm position-relative cursor-pointer`}
                                  onClick={() => {
                                    if (ud.companyId) {
                                      const win = window.open(
                                        `${RouteKeys.Company360View}/${ud.companyId || ""}`,
                                        "_blank"
                                      );
                                      if (win) {
                                        win.focus();
                                      }

                                      // history.push(
                                      //   `${RouteKeys.Company360View}/${
                                      //     ud.companyId || ""
                                      //   }`
                                      // );
                                    }
                                  }}
                                >
                                  {ud.currentStatus === "Available"
                                    ? "Available Unit"
                                    : ud.currentStatus === "Leased"
                                    ? "Leased Unit"
                                    : ud.tenantsName}
                                  {ud.currentStatus === "Available" && (
                                    <i
                                      className={`${
                                        expandedRows[ud?.unitName]
                                          ? "icon-chevron-down"
                                          : "icon-chevron-up"
                                      } position-absolute right-0 align-y-center px-2 mx-1`}
                                      onClick={() => toggleRow(ud?.unitName)}
                                    />
                                  )}
                                </div>
                              </div>
                              {expandedRows[ud?.unitName] && (
                                <div
                                  className="bg-gray-100 py-3 px-4 mt-1 w-100 order-1"
                                  style={{ marginLeft: "24px" }}
                                >
                                  <div className="row align-items-center">
                                    <div className="col-md-9 col-12">
                                      <div className="row">
                                        <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                          <div className="text-gray-500">Type</div>
                                          <div>{ud?.propertyTypeName || props?.project?.type}</div>
                                        </div>
                                        <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                          <div className="text-gray-500">Area</div>
                                          <div>{ud?.unitArea ? `${ud?.unitArea} Sq. Ft` : ""} </div>
                                        </div>
                                        <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                          <div className="text-gray-500">Floor Offered</div>
                                          <div> {td?.floorNum === 0 ? "G" : td?.floorNum}</div>
                                        </div>
                                        <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                          <div className="text-gray-500">Status</div>
                                          <div>
                                            {ud?.propertyCurrentStatus
                                              ? ud?.propertyCurrentStatus
                                              : "Ready to move"}
                                          </div>
                                        </div>
                                        <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                          <div className="text-gray-500">Type of Facility</div>
                                          <div>{ud?.interior ? ud?.interior : "Pre Furnished"}</div>
                                        </div>
                                        {props.project?.unitDetails?.find(
                                          (x) => x.propertyID === ud.propertyID
                                        )?.propertyId && (
                                          <div className="col-md-2 col-sm-3 col-4 font-sm mb-2 pt-1">
                                            <button
                                              onClick={() =>
                                                props.onView?.(
                                                  props.project?.unitDetails?.find(
                                                    (x) => x.propertyID === ud.propertyID
                                                  )?.propertyId!
                                                )
                                              }
                                              className="btn btn-primary px-1 font-sm font-weight-semibold py-1 mt-1 rounded"
                                            >
                                              View Details
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* <div className="col-md-3 col-12 text-right">
                                    <button className="btn btn-primary px-3 font-sm font-weight-semibold py-2 rounded">
                                      View Details
                                    </button>
                                  </div> */}
                                  </div>
                                </div>
                              )}
                            </>
                          ))}
                        </div>
                      ) : (
                        <div>No Unit Details Found</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div>No data found</div>
                )}
              </>
            )}

            {ViewTypes.Timeline === viewType && (
              <div className="col-12 mt-2">
                <TenancyTimeline
                  projectID={props.project?.projectID}
                  projectName={props.project?.projectName}
                  className="pt-5"
                  showTitle={false}
                ></TenancyTimeline>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyUnit;
